<script setup>
import { useInfiniteScroll, useThrottleFn, useScroll } from '@vueuse/core';

const preferencesStore = usePreferencesStore();

const emit = defineEmits(['loadMore', 'changePage']);

const props = defineProps({
  pending: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Array,
    default: () => [],
  },
  total: {
    type: Number,
    default: 1,
  },
  count: {
    type: Number,
    default: 6,
  },
  end: {
    type: Boolean,
    default: false,
  },
  page: {
    type: Number,
    default: 1,
  },
  filterRef: {
    type: Object,
    default: null,
  },
  promoteThird: {
    type: Boolean,
    default: false,
  },
  myFavesPage: {
    type: Boolean,
    default: false,
  },
  isBlogPage: {
    type: Boolean,
    default: false,
  },
});

const listRef = ref(null);

const isListViewType = computed(() => {
  return preferencesStore.GET_VIEW_TYPE === 'list' || props.isBlogPage;
});

useInfiniteScroll(
  listRef,
  () => {
    if (!props.end && !props.pending) emit('loadMore');
  },
  {
    distance: 1500,
    interval: 500,
    offset: {
      bottom: 500,
    },
  },
);

const { arrivedState } = useScroll(listRef);

const setScrollPosition = useThrottleFn(() => {
  scrollTo({
    top: props.filterRef?.top - 80 || 0,
    behavior: 'smooth',
  });
}, 1000);

provide('pagination', {
  updateCurrentPage: (page) => {
    emit('changePage', page);
  },
});
</script>

<template>
  <section
    :class="[
      'relative transition-all',
      myFavesPage ? '' : 'mt-2 rounded-t-3xl',
      !isBlogPage ? 'dark:bg-primary-blue-light' : '',
    ]"
    :style="`min-height: calc(100vh - ${(filterRef?.height || 80) + 80}px)`"
  >
    <ul
      ref="listRef"
      class="no-scrollbar size-full overflow-scroll overscroll-auto px-3 py-8 transition-all dark:rounded-t-3xl sm:px-10"
      :style="
        myFavesPage
          ? 'max-height: calc(85vh - 80px)'
          : `max-height: calc(100vh - ${filterRef?.height + 80 || 80}px)`
      "
      :class="{
        'promote-third': promoteThird,
        'grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 lg:gap-3 2xl:grid-cols-4 2xl:gap-4':
          !isListViewType,
        'flex flex-col ': isListViewType,
        'overscroll-contain': !arrivedState.top,

        'gap-8': isBlogPage,
      }"
      @scroll="setScrollPosition()"
    >
      <slot name="content" />

      <template v-if="!end">
        <slot name="skeleton" />
      </template>
    </ul>

    <slot name="empty">
      <ClientOnly>
        <p
          v-if="!data.length && !pending"
          class="absolute inset-1/2 w-max -translate-x-1/2 text-3xl font-bold dark:text-white"
        >
          No content Found.
        </p>
      </ClientOnly>
    </slot>
  </section>
</template>
